import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import '../styles/AdminDashboard.css';

const API_BASE = window.location.port === '3000' ? 'http://localhost:5000' : '';

export default function AdminDashboard({ token }) {
  const navigate = useNavigate();
  const [applicants, setApplicants] = useState([]);
  const [stats, setStats] = useState({ total: 0, approved: 0, pending: 0, rejected: 0, avgScore: '0.0', completedExams: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterTab, setFilterTab] = useState('all');

  // Modal states
  const [selectedApp, setSelectedApp] = useState(null);
  const [modalType, setModalType] = useState(null); // 'approve' | 'reject' | 'view' | 'result' | 'allow-edit'
  const [testDate, setTestDate] = useState('2026-10-25');
  const [testTime, setTestTime] = useState('09:00');
  const [examRoom, setExamRoom] = useState('Turon universiteti bosh binosi, 214-auditoriya (Qarshi shahri, Nasaf koʻchasi, 1/14-uy)');
  const [examNotes, setExamNotes] = useState("Imtihon Turon universitetida offline (an'anaviy) shaklda oʻtkaziladi. Shaxsni tasdiqlovchi pasport yoki ID-karta bilan 15 daqiqa oldin belgilangan xonaga yetib kelishingiz soʻraladi.");
  const [rejectReason, setRejectReason] = useState('');
  const [editReason, setEditReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null); // { title, filename, url }

  // Offline Exam Result state (Admin grading)
  const [examScore, setExamScore] = useState(85);
  const [examResultStatus, setExamResultStatus] = useState('passed');
  const [examCertNumber, setExamCertNumber] = useState('');
  const [examResultNotes, setExamResultNotes] = useState('');
  const [resultPdfFile, setResultPdfFile] = useState(null);

  // Tab switcher
  const [mainTab, setMainTab] = useState('applications'); // 'applications' | 'users' | 'services'

  // User management state
  const [users, setUsers] = useState([]);
  const [userLoading, setUserLoading] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [showAllPasswords, setShowAllPasswords] = useState(true);
  const [userModal, setUserModal] = useState(null); // 'add' | 'edit' | null
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({ name: '', email: '', password: '', role: 'applicant' });
  const [userFormError, setUserFormError] = useState('');

  // Service management state
  const [services, setServices] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [serviceForm, setServiceForm] = useState({
    deadline: '',
    receptionPeriod: '',
    receptionStart: '',
    receptionEnd: '',
    workingHours: '',
    requirements: '',
    priceText: '',
    processingDays: '',
    timeToApply: 8,
    status: 'active'
  });
  const [serviceSaveMsg, setServiceSaveMsg] = useState('');

  // Reviews management state
  const [reviews, setReviews] = useState([]);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [selectedReviewService, setSelectedReviewService] = useState('all');
  const [reviewModal, setReviewModal] = useState(null); // 'add' or 'edit' or null
  const [reviewForm, setReviewForm] = useState({ id: null, serviceId: 'phd-exam', userName: '', rating: 5, comment: '' });
  const [reviewSaveMsg, setReviewSaveMsg] = useState('');

  useEffect(() => {
    loadData();
    loadUsers();
    loadServices();
    loadReviews();
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (editingService) setEditingService(null);
        if (userModal) setUserModal(null);
        if (modalType) setModalType(null);
        if (reviewModal) setReviewModal(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [editingService, userModal, modalType, reviewModal]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [appRes, statsRes] = await Promise.all([
        axios.get('/api/admin/applicants'),
        axios.get('/api/admin/stats').catch(() => ({ data: null }))
      ]);

      const sortedApps = (appRes.data || []).sort((a, b) => (Number(a.id) || 0) - (Number(b.id) || 0));
      setApplicants(sortedApps);
      if (statsRes.data) {
        setStats(statsRes.data);
      }
    } catch (err) {
      console.error('Admin load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      setUserLoading(true);
      const res = await axios.get('/api/admin/users');
      if (res.data && res.data.users) {
        setUsers(res.data.users);
      }
    } catch (err) {
      console.error('Users load error:', err);
    } finally {
      setUserLoading(false);
    }
  };

  const openAddUserModal = () => {
    setEditingUser(null);
    setUserForm({ name: '', email: '', password: '', role: 'applicant' });
    setUserFormError('');
    setUserModal('add');
  };

  const openEditUserModal = (u) => {
    setEditingUser(u);
    setUserForm({ name: u.name, email: u.email, password: u.password, role: u.role || 'applicant' });
    setUserFormError('');
    setUserModal('edit');
  };

  const handleSaveUser = async (e) => {
    e.preventDefault();
    if (!userForm.name.trim() || !userForm.email.trim() || !userForm.password.trim()) {
      setUserFormError('Iltimos, barcha maydonlarni toʻldiring');
      return;
    }

    try {
      setActionLoading(true);
      setUserFormError('');
      if (userModal === 'add') {
        await axios.post('/api/admin/users', userForm);
      } else {
        await axios.put(`/api/admin/users/${editingUser.id}`, userForm);
      }
      setUserModal(null);
      loadUsers();
    } catch (err) {
      setUserFormError(err.response?.data?.error || 'Xatolik yuz berdi');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Haqiqatan ham «${userName}» foydalanuvchisini oʻchirmoqchimisiz?`)) {
      return;
    }
    try {
      await axios.delete(`/api/admin/users/${userId}`);
      loadUsers();
    } catch (err) {
      alert(err.response?.data?.error || 'Foydalanuvchini oʻchirishda xatolik');
    }
  };

  const loadServices = async () => {
    try {
      setServicesLoading(true);
      const res = await axios.get('/api/services');
      if (res.data && res.data.services) {
        setServices(res.data.services);
      }
    } catch (err) {
      console.error('Services load error:', err);
    } finally {
      setServicesLoading(false);
    }
  };

  const openEditServiceModal = (svc) => {
    setEditingService(svc);
    let formattedDeadline = '';
    if (svc.deadline) {
      try {
        const d = new Date(svc.deadline);
        if (!isNaN(d.getTime())) {
          const pad = (n) => String(n).padStart(2, '0');
          formattedDeadline = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
        }
      } catch (e) {
        formattedDeadline = '';
      }
    }

    setServiceForm({
      deadline: formattedDeadline,
      receptionPeriod: svc.receptionPeriod || '',
      receptionStart: svc.receptionStart || '',
      receptionEnd: svc.receptionEnd || '',
      workingHours: svc.workingHours || '',
      requirements: svc.requirements || '',
      priceText: svc.priceText || '',
      processingDays: svc.processingDays || '',
      timeToApply: svc.timeToApply !== undefined ? svc.timeToApply : 8,
      status: svc.status || 'active'
    });
    setServiceSaveMsg('');
  };

  const UZ_MONTH_NAMES = [
    'yanvar', 'fevral', 'mart', 'aprel', 'may', 'iyun',
    'iyul', 'avgust', 'sentabr', 'oktabr', 'noyabr', 'dekabr'
  ];

  const buildReceptionPeriodStr = (startStr, endStr) => {
    try {
      const sParts = String(startStr || '').split('.');
      const eParts = String(endStr || '').split('.');
      if (sParts.length === 3 && eParts.length === 3) {
        const sDay = parseInt(sParts[0], 10);
        const sMonth = UZ_MONTH_NAMES[parseInt(sParts[1], 10) - 1] || 'oktabr';
        const sYear = sParts[2];
        const eDay = parseInt(eParts[0], 10);
        const eMonth = UZ_MONTH_NAMES[parseInt(eParts[1], 10) - 1] || 'noyabr';
        const eYear = eParts[2];

        if (sYear === eYear && sMonth === eMonth) {
          return `${sYear}-yil ${sDay}-dan ${eDay}-${eMonth}gacha`;
        }
        if (sYear === eYear) {
          return `${sYear}-yil ${sDay}-${sMonth}dan ${eDay}-${eMonth}gacha`;
        }
        return `${sYear}-yil ${sDay}-${sMonth}dan ${eYear}-yil ${eDay}-${eMonth}gacha`;
      }
    } catch (e) {
      // ignore
    }
    return '';
  };

  const applyDeadlinePreset = (daysToAdd, specificDate) => {
    const pad = (n) => String(n).padStart(2, '0');
    let targetDate;
    if (specificDate) {
      targetDate = new Date(specificDate);
    } else {
      const now = new Date();
      targetDate = new Date(now.getTime() + daysToAdd * 24 * 60 * 60 * 1000);
      targetDate.setHours(23, 59, 0, 0);
    }

    const day = pad(targetDate.getDate());
    const month = pad(targetDate.getMonth() + 1);
    const year = targetDate.getFullYear();
    const hours = pad(targetDate.getHours());
    const minutes = pad(targetDate.getMinutes());

    const formattedDeadline = `${year}-${month}-${day}T${hours}:${minutes}`;
    const receptionEnd = `${day}.${month}.${year}`;
    const receptionPeriod = buildReceptionPeriodStr(serviceForm.receptionStart || '05.10.2026', receptionEnd) || `${year}-yil 5-oktabrdan ${receptionEnd}gacha`;

    setServiceForm(prev => ({
      ...prev,
      deadline: formattedDeadline,
      receptionEnd,
      receptionPeriod,
      status: 'active'
    }));
  };

  const handleDeadlineChange = (val) => {
    const updated = { ...serviceForm, deadline: val };
    if (val) {
      try {
        const d = new Date(val);
        if (!isNaN(d.getTime())) {
          const pad = (n) => String(n).padStart(2, '0');
          const day = pad(d.getDate());
          const month = pad(d.getMonth() + 1);
          const year = d.getFullYear();
          updated.receptionEnd = `${day}.${month}.${year}`;
          const periodStr = buildReceptionPeriodStr(updated.receptionStart || '05.10.2026', updated.receptionEnd);
          if (periodStr) {
            updated.receptionPeriod = periodStr;
          }
        }
      } catch (e) {
        // ignore
      }
    }
    setServiceForm(updated);
  };

  const handleReceptionEndChange = (val) => {
    const updated = { ...serviceForm, receptionEnd: val };
    const parts = val.trim().split('.');
    if (parts.length === 3) {
      const day = parts[0].padStart(2, '0');
      const month = parts[1].padStart(2, '0');
      const year = parts[2];
      if (year.length === 4) {
        updated.deadline = `${year}-${month}-${day}T23:59`;
        const periodStr = buildReceptionPeriodStr(updated.receptionStart || '05.10.2026', `${day}.${month}.${year}`);
        if (periodStr) {
          updated.receptionPeriod = periodStr;
        }
      }
    }
    setServiceForm(updated);
  };

  const handleSaveService = async (e) => {
    e.preventDefault();
    if (!editingService) return;

    try {
      setActionLoading(true);
      let isoDeadline = serviceForm.deadline;
      if (serviceForm.deadline) {
        const d = new Date(serviceForm.deadline);
        if (!isNaN(d.getTime())) {
          isoDeadline = d.toISOString();
        }
      }

      const payload = {
        ...serviceForm,
        deadline: isoDeadline,
        timeToApply: parseInt(serviceForm.timeToApply, 10) || 8
      };
      delete payload.rating;
      delete payload.applicantCount;

      const res = await axios.put(`/api/admin/services/${editingService.id}`, payload);
      if (res.data && res.data.success) {
        setServiceSaveMsg('Xizmat sozlamalari va talablari muvaffaqiyatli saqlandi!');
        setTimeout(() => {
          setEditingService(null);
          setServiceSaveMsg('');
        }, 1000);
        loadServices();
      }
    } catch (err) {
      console.error('Service save error:', err);
      alert(err.response?.data?.error || err.message || 'Xatolik yuz berdi');
    } finally {
      setActionLoading(false);
    }
  };

  const loadReviews = async (sId) => {
    try {
      setReviewLoading(true);
      const url = sId && sId !== 'all' ? `/api/admin/reviews?serviceId=${sId}` : '/api/admin/reviews';
      const res = await axios.get(url);
      if (res.data && res.data.reviews) {
        setReviews(res.data.reviews);
      }
    } catch (err) {
      console.error('Reviews load error:', err);
    } finally {
      setReviewLoading(false);
    }
  };

  const openAddReviewModal = (svcId = 'phd-exam') => {
    setReviewForm({
      id: null,
      serviceId: svcId === 'all' ? 'phd-exam' : svcId,
      userName: '',
      rating: 5,
      comment: ''
    });
    setReviewSaveMsg('');
    setReviewModal('add');
  };

  const openEditReviewModal = (rev) => {
    setReviewForm({
      id: rev.id,
      serviceId: rev.serviceId || 'phd-exam',
      userName: rev.userName || '',
      rating: rev.rating || 5,
      comment: rev.comment || ''
    });
    setReviewSaveMsg('');
    setReviewModal('edit');
  };

  const handleSaveReview = async (e) => {
    e.preventDefault();
    if (!reviewForm.userName.trim()) {
      alert('Iltimos, talabgor ism-sharifini kiriting');
      return;
    }
    try {
      setActionLoading(true);
      if (reviewModal === 'add') {
        await axios.post(`/api/admin/services/${reviewForm.serviceId}/reviews`, {
          userName: reviewForm.userName,
          rating: Number(reviewForm.rating),
          comment: reviewForm.comment
        });
      } else {
        await axios.put(`/api/admin/services/${reviewForm.serviceId}/reviews/${reviewForm.id}`, {
          userName: reviewForm.userName,
          rating: Number(reviewForm.rating),
          comment: reviewForm.comment
        });
      }
      setReviewSaveMsg('Sharh muvaffaqiyatli saqlandi va xizmat oʻrtacha bahosi yangilandi!');
      await loadReviews(selectedReviewService);
      await loadServices();
      setTimeout(() => {
        setReviewModal(null);
        setReviewSaveMsg('');
      }, 1000);
    } catch (err) {
      alert('Sharhni saqlashda xatolik: ' + (err.response?.data?.error || err.message));
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteReview = async (serviceId, reviewId) => {
    if (!window.confirm('Haqiqatan ham ushbu sharhni oʻchirib tashlamoqchimisiz? Natijada xizmatning oʻrtacha reytingi qayta hisoblanadi.')) {
      return;
    }
    try {
      setActionLoading(true);
      await axios.delete(`/api/admin/services/${serviceId}/reviews/${reviewId}`);
      await loadReviews(selectedReviewService);
      await loadServices();
    } catch (err) {
      alert('Oʻchirishda xatolik: ' + (err.response?.data?.error || err.message));
    } finally {
      setActionLoading(false);
    }
  };

  const handleApproveSubmit = async () => {
    if (!selectedApp) return;
    try {
      setActionLoading(true);
      await axios.post(`/api/admin/approve-applicant/${selectedApp.id}`, {
        testDate,
        testTime,
        examRoom,
        examNotes
      });
      setModalType(null);
      setSelectedApp(null);
      loadData();
    } catch (err) {
      alert('Tasdiqlashda xatolik: ' + (err.response?.data?.error || err.message));
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectSubmit = async () => {
    if (!selectedApp) return;
    if (!rejectReason.trim()) {
      alert('Iltimos, rad qilish sababini koʻrsating');
      return;
    }
    try {
      setActionLoading(true);
      await axios.post(`/api/admin/reject-applicant/${selectedApp.id}`, {
        reason: rejectReason
      });
      setModalType(null);
      setSelectedApp(null);
      setRejectReason('');
      loadData();
    } catch (err) {
      alert('Rad qilishda xatolik: ' + (err.response?.data?.error || err.message));
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteApplicant = async (appId, appName) => {
    const confirmed = window.confirm(
      `Diqqat! "${appName}"ning arizasini va serverga yuklangan barcha rasmiy hujjatlarini (PDF) butunlay oʻchirib tashlamoqchimisiz?\n\nBu amalni ortga qaytarib boʻlmaydi!`
    );
    if (!confirmed) return;

    try {
      setActionLoading(true);
      const res = await axios.delete(`/api/admin/applicants/${appId}`);
      if (selectedApp && selectedApp.id === appId) {
        setModalType(null);
        setSelectedApp(null);
      }
      await loadData();
      alert(res.data.message || 'Ariza va uning fayllari serverdan muvaffaqiyatli oʻchirildi!');
    } catch (err) {
      alert('Arizani oʻchirishda xatolik: ' + (err.response?.data?.error || err.message));
    } finally {
      setActionLoading(false);
    }
  };

  const openModal = (app, type) => {
    setSelectedApp(app);
    setModalType(type);
    if (type === 'approve') {
      setTestDate(app.testDate || app.test_date || '2026-10-25');
      setTestTime(app.testTime || app.test_time || '09:00');
      setExamRoom(app.examRoom || app.exam_room || 'Turon universiteti bosh binosi, 214-auditoriya (Qarshi shahri, Nasaf koʻchasi, 1/14-uy)');
      setExamNotes(app.examNotes || app.exam_notes || "Imtihon Turon universitetida offline (an'anaviy) shaklda oʻtkaziladi. Shaxsni tasdiqlovchi pasport yoki ID-karta bilan 15 daqiqa oldin belgilangan auditoriyaga yetib kelishingiz soʻraladi.");
    }
    if (type === 'reject') {
      setRejectReason(app.rejectionReason || app.rejection_reason || '');
    }
    if (type === 'result') {
      const existingScore = app.score !== null && app.score !== undefined ? app.score : (app.examResult?.score !== undefined ? app.examResult.score : 85);
      setExamScore(existingScore);
      setExamResultStatus(app.status === 'passed' ? 'passed' : (app.status === 'failed' ? 'failed' : (existingScore >= 60 ? 'passed' : 'failed')));
      setExamCertNumber(app.certificateNumber || app.examResult?.certificateNumber || `№ 2026-${String(Math.floor(1000 + Math.random() * 9000))}`);
      setExamResultNotes(app.examResult?.resultNotes || 'Talabgor malakaviy imtihon dasturi boʻyicha berilgan barcha savollarga toʻliq va ilmiy asoslangan javob berdi. Komissiya qarori bilan ijobiy baholandi.');
      setResultPdfFile(null);
    }
    if (type === 'allow-edit') {
      setEditReason(app.editReason || 'Hujjatlar sifati yetarli emas yoki maʼlumotlarda xatolik aniqlandi. Iltimos, tekshirib toʻgʻrilang.');
    }
  };

  const handleAllowEditSubmit = async () => {
    if (!selectedApp) return;
    try {
      setActionLoading(true);
      await axios.post(`/api/admin/allow-edit/${selectedApp.id}`, {
        reason: editReason
      });
      setModalType(null);
      setSelectedApp(null);
      setEditReason('');
      loadData();
    } catch (err) {
      alert('Tahrirlashga ruxsat berishda xatolik: ' + (err.response?.data?.error || err.message));
    } finally {
      setActionLoading(false);
    }
  };

  const handleRevokeEdit = async (appId) => {
    try {
      setActionLoading(true);
      await axios.post(`/api/admin/revoke-edit/${appId}`);
      setModalType(null);
      setSelectedApp(null);
      loadData();
    } catch (err) {
      alert('Ruxsatni bekor qilishda xatolik: ' + (err.response?.data?.error || err.message));
    } finally {
      setActionLoading(false);
    }
  };

  const handleResultSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!selectedApp) return;
    try {
      setActionLoading(true);
      const formData = new FormData();
      formData.append('score', examScore);
      formData.append('resultStatus', examResultStatus);
      formData.append('certificateNumber', examCertNumber);
      formData.append('resultNotes', examResultNotes);
      if (resultPdfFile) {
        formData.append('resultFile', resultPdfFile);
      }

      await axios.post(`/api/admin/submit-exam-result/${selectedApp.id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setModalType(null);
      setSelectedApp(null);
      await loadData();
      alert('Offline imtihon natijasi muvaffaqiyatli saqlandi va talabgor shaxsiy kabinetida eʼlon qilindi!');
    } catch (err) {
      alert('Natijani saqlashda xatolik: ' + (err.response?.data?.error || err.message));
    } finally {
      setActionLoading(false);
    }
  };

  const handleExportExcel = () => {
    const statusMap = {
      approved: { text: 'Tasdiqlangan (Ruxsat berilgan)', bg: '#dcfce7', color: '#15803d' },
      pending: { text: 'Kutilmoqda (Tekshiruvda)', bg: '#fef3c7', color: '#b45309' },
      rejected: { text: 'Rad etilgan', bg: '#fee2e2', color: '#b91c1c' },
      passed: { text: 'Imtihondan oʻtgan', bg: '#d1fae5', color: '#047857' },
      failed: { text: 'Imtihondan oʻtmagan', bg: '#fef2f2', color: '#dc2626' }
    };

    const sortedForExcel = [...applicants].sort((a, b) => (Number(a.id) || 0) - (Number(b.id) || 0));
    const rowsHtml = sortedForExcel.map((a, idx) => {
      const st = statusMap[a.status] || { text: a.status || '-', bg: '#ffffff', color: '#000000' };
      const rowBg = idx % 2 === 0 ? '#ffffff' : '#f8fafc';
      const jshshir = (a.passportNumber || a.passport_number || '').toString();
      const phone = (a.phoneNumber || a.phone_number || a.phone || '-').toString();
      const venue = a.examRoom || a.exam_room || (a.status === 'approved' ? 'Turon universiteti bosh binosi, 214-auditoriya' : '-');

      return `
        <tr style="background-color: ${rowBg};">
          <td style="border: 1px solid #cbd5e1; padding: 10px; text-align: center; font-weight: bold;">${a.id}</td>
          <td style="border: 1px solid #cbd5e1; padding: 10px; font-weight: bold; color: #0f2b5c;">${a.fullName || a.full_name || ''}</td>
          <td style="border: 1px solid #cbd5e1; padding: 10px; mso-number-format:'\\@'; font-weight: bold; color: #0284c7; text-align: center;">${phone}</td>
          <td style="border: 1px solid #cbd5e1; padding: 10px; text-align: center; mso-number-format:'\\@'; font-family: monospace;">${jshshir}</td>
          <td style="border: 1px solid #cbd5e1; padding: 10px;">${a.specialization || ''}</td>
          <td style="border: 1px solid #cbd5e1; padding: 10px;">${a.otmName || a.otm_name || ''}</td>
          <td style="border: 1px solid #cbd5e1; padding: 10px; text-align: center; background-color: ${st.bg}; color: ${st.color}; font-weight: bold;">${st.text}</td>
          <td style="border: 1px solid #cbd5e1; padding: 10px; text-align: center;">${a.testDate || a.test_date || '-'}</td>
          <td style="border: 1px solid #cbd5e1; padding: 10px; text-align: center;">${a.testTime || a.test_time || '-'}</td>
          <td style="border: 1px solid #cbd5e1; padding: 10px;">${venue}</td>
        </tr>
      `;
    }).join('');

    const excelTemplate = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
      <head>
        <meta http-equiv="Content-Type" content="text/html; charset=UTF-8">
        <!--[if gte mso 9]>
        <xml>
          <x:ExcelWorkbook>
            <x:ExcelWorksheets>
              <x:ExcelWorksheet>
                <x:Name>Talabgorlar</x:Name>
                <x:WorksheetOptions>
                  <x:DisplayGridlines/>
                </x:WorksheetOptions>
              </x:ExcelWorksheet>
            </x:ExcelWorksheets>
          </x:ExcelWorkbook>
        </xml>
        <![endif]-->
        <style>
          table { font-family: Calibri, 'Segoe UI', Arial, sans-serif; font-size: 11pt; border-collapse: collapse; }
          th { background-color: #0f2b5c; color: #ffffff; font-weight: bold; padding: 12px 10px; border: 1px solid #0f2b5c; text-align: center; }
          td { border: 1px solid #cbd5e1; vertical-align: middle; }
        </style>
      </head>
      <body>
        <table style="width: 100%;">
          <tr>
            <td colspan="10" style="font-size: 16pt; font-weight: bold; color: #0f2b5c; text-align: center; padding: 16px 0; border: none;">
              TURON UNIVERSITETI • ILMIY DARAJALAR UCHUN MALAKAVIY IMTIHON TALABGORLARI ROʻYXATI
            </td>
          </tr>
          <tr>
            <td colspan="10" style="font-size: 10pt; color: #64748b; text-align: center; padding-bottom: 12px; border: none;">
              Eksport sanasi: ${new Date().toLocaleDateString('uz-UZ')} ${new Date().toLocaleTimeString('uz-UZ')} | Jami talabgorlar: ${applicants.length} nafar | Offline imtihon komissiyasi
            </td>
          </tr>
          <thead>
            <tr>
              <th style="width: 50px;">ID</th>
              <th style="width: 250px;">F.I.Sh.</th>
              <th style="width: 160px; background-color: #0284c7;">Telefon raqami</th>
              <th style="width: 160px;">JSHSHIR</th>
              <th style="width: 300px;">Fan / Ixtisoslik</th>
              <th style="width: 220px;">Biriktirilgan OTM</th>
              <th style="width: 160px;">Holati (Status)</th>
              <th style="width: 130px;">Imtihon sanasi</th>
              <th style="width: 90px;">Vaqti</th>
              <th style="width: 240px;">Bino / Auditoriya (Joyi)</th>
            </tr>
          </thead>
          <tbody>
            ${rowsHtml}
          </tbody>
        </table>
      </body>
      </html>
    `;

    const blob = new Blob(['\ufeff', excelTemplate], { type: 'application/vnd.ms-excel;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Turon_PhD_Talabgorlar_${new Date().toISOString().slice(0, 10)}.xls`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const filteredApplicants = applicants
    .filter(a => {
      const nameMatch = (a.fullName || a.full_name || '').toLowerCase().includes(search.toLowerCase());
      const specMatch = (a.specialization || '').toLowerCase().includes(search.toLowerCase());
      const passMatch = (a.passportNumber || a.passport_number || '').includes(search);

      const matchesSearch = nameMatch || specMatch || passMatch;
      let matchesTab = filterTab === 'all' || a.status === filterTab;
      if (filterTab === 'results') {
        matchesTab = a.status === 'passed' || a.status === 'failed';
      }
      return matchesSearch && matchesTab;
    })
    .sort((a, b) => (Number(a.id) || 0) - (Number(b.id) || 0));

  const filteredUsers = users.filter((u) => {
    const q = userSearch.toLowerCase();
    return (
      (u.name || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q) ||
      (u.role || '').toLowerCase().includes(q)
    );
  });

  return (
    <div className="admin-page-wrapper">
      <div className="portal-breadcrumb-strip">
        <div className="container">
          <div className="breadcrumb-nav">
            <Link to="/portal">Bosh sahifa</Link>
            <span className="crumb-arrow">/</span>
            <span className="crumb-current">Admin boshqaruv paneli</span>
          </div>
        </div>
      </div>

      <div className="container admin-container">
        {/* Top header */}
        <div className="admin-header-row">
          <div>
            <span className="admin-sub-tag">TURON UNIVERSITETI • PORTAL BOSHQARUVI</span>
            <h1 className="admin-main-title">Interaktiv xizmatlar portali maʼmuriyati</h1>
          </div>

          <div className="admin-top-actions">
            {mainTab === 'applications' && (
              <button className="btn-admin-action" onClick={handleExportExcel}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
                <span>Roʻyxatni yuklab olish (Excel)</span>
              </button>
            )}
            {mainTab === 'users' && (
              <button className="btn-admin-action btn-add-user-header" onClick={openAddUserModal}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                <span>Yangi foydalanuvchi qoʻshish</span>
              </button>
            )}
            <button
              className="btn-admin-action btn-view-portal"
              onClick={() => navigate('/portal')}
              title="Foydalanuvchilar koʻradigan ommaviy portalga oʻtish"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"></circle><line x1="2" y1="12" x2="22" y2="12"></line><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1 4-10z"></path></svg>
              <span>Portalni koʻrish</span>
            </button>
            <button
              className="btn-admin-refresh"
              onClick={() => { loadData(); loadUsers(); loadServices(); loadReviews(); }}
              title="Yangilash"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 4 23 10 17 10"></polyline><polyline points="1 20 1 14 7 14"></polyline><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path></svg>
            </button>
          </div>
        </div>

        {/* Asosiy boʻlimlar navigatsiyasi (Tab switcher) */}
        <div className="admin-main-tabs-bar">
          <button
            type="button"
            className={`admin-main-tab ${mainTab === 'applications' ? 'active' : ''}`}
            onClick={() => setMainTab('applications')}
          >
            📑 Arizalar va imtihonlar monitoringi ({applicants.length})
          </button>
          <button
            type="button"
            className={`admin-main-tab ${mainTab === 'users' ? 'active' : ''}`}
            onClick={() => {
              setMainTab('users');
              loadUsers();
            }}
          >
            👥 Foydalanuvchilar (Login va Parollar) ({users.length})
          </button>
          <button
            type="button"
            className={`admin-main-tab ${mainTab === 'services' ? 'active' : ''}`}
            onClick={() => {
              setMainTab('services');
              loadServices();
            }}
          >
            ⚙️ Xizmatlar sozlamalari va Muddatlar (Deadline) ({services.length})
          </button>
          <button
            type="button"
            className={`admin-main-tab ${mainTab === 'reviews' ? 'active' : ''}`}
            onClick={() => {
              setMainTab('reviews');
              loadReviews();
            }}
          >
            ⭐ Xizmat sharhlari va Baholar ({reviews.length})
          </button>
        </div>

        {/* 1-BOʻLIM: ARIZALAR MONITORINGI */}
        {mainTab === 'applications' && (
          <>
            {/* Stats Grid */}
            <div className="admin-stats-grid">
              <div className="stat-metric-card">
                <div className="stat-metric-icon blue">📁</div>
                <div className="stat-metric-data">
                  <span className="stat-number">{stats.total || applicants.length}</span>
                  <span className="stat-label">Jami arizalar</span>
                </div>
              </div>

              <div className="stat-metric-card">
                <div className="stat-metric-icon amber">⏳</div>
                <div className="stat-metric-data">
                  <span className="stat-number">{stats.pending}</span>
                  <span className="stat-label">Koʻrib chiqilmoqda</span>
                </div>
              </div>

              <div className="stat-metric-card">
                <div className="stat-metric-icon green">✓</div>
                <div className="stat-metric-data">
                  <span className="stat-number">{stats.approved}</span>
                  <span className="stat-label">Tasdiqlangan</span>
                </div>
              </div>

              <div className="stat-metric-card">
                <div className="stat-metric-icon red">✕</div>
                <div className="stat-metric-data">
                  <span className="stat-number">{stats.rejected}</span>
                  <span className="stat-label">Rad etilgan</span>
                </div>
              </div>

              <div className="stat-metric-card">
                <div className="stat-metric-icon purple">🏆</div>
                <div className="stat-metric-data">
                  <span className="stat-number">{stats.completedExams > 0 ? `${stats.avgScore}%` : '0%'}</span>
                  <span className="stat-label">Oʻrtacha imtihon bali ({stats.completedExams || 0} ta topshirilgan)</span>
                </div>
              </div>
            </div>

            {/* Filter bar */}
            <div className="admin-table-controls">
              <div className="table-tabs">
                <button className={filterTab === 'all' ? 'active' : ''} onClick={() => setFilterTab('all')}>
                  Barchasi ({applicants.length})
                </button>
                <button className={filterTab === 'pending' ? 'active' : ''} onClick={() => setFilterTab('pending')}>
                  Kutilayotganlar ({applicants.filter(a => a.status === 'pending').length})
                </button>
                <button className={filterTab === 'approved' ? 'active' : ''} onClick={() => setFilterTab('approved')}>
                  Tasdiqlanganlar ({applicants.filter(a => a.status === 'approved').length})
                </button>
                <button className={filterTab === 'rejected' ? 'active' : ''} onClick={() => setFilterTab('rejected')}>
                  Rad etilganlar ({applicants.filter(a => a.status === 'rejected').length})
                </button>
                {applicants.some(a => a.status === 'passed' || a.status === 'failed') && (
                  <button className={filterTab === 'results' ? 'active' : ''} onClick={() => setFilterTab('results')}>
                    🎓 Imtihon oʻtganlar ({applicants.filter(a => a.status === 'passed' || a.status === 'failed').length})
                  </button>
                )}
              </div>

              <div className="table-search-box">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
                <input
                  type="text"
                  placeholder="F.I.Sh., JSHSHIR yoki fan nomi..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>

            {/* Applicants Table */}
            <div className="admin-table-wrapper">
              {loading ? (
                <div className="table-loading-box">
                  <div className="dash-spinner"></div>
                  <span>Maʼlumotlar yuklanmoqda...</span>
                </div>
              ) : (
                <table className="admin-data-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Talabgor F.I.Sh.</th>
                      <th>Telefon raqami</th>
                      <th>JSHSHIR</th>
                      <th>Imtihon fani</th>
                      <th>Biriktirilgan OTM</th>
                      <th>Holat</th>
                      <th>Offline imtihon jadvali</th>
                      <th>Amallar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredApplicants.length === 0 ? (
                      <tr>
                        <td colSpan="9" className="empty-table-cell" style={{ textAlign: 'center', padding: '48px 20px', color: '#64748b' }}>
                          <div style={{ fontSize: '36px', marginBottom: '10px' }}>📂</div>
                          <strong style={{ display: 'block', fontSize: '16px', color: '#1e293b', marginBottom: '6px' }}>
                            Hozircha arizalar mavjud emas
                          </strong>
                          <span style={{ fontSize: '13.5px', color: '#64748b' }}>
                            Talabgorlar tomonidan portal orqali topshirilgan yangi arizalar shu yerda koʻrinadi.
                          </span>
                        </td>
                      </tr>
                    ) : (
                      filteredApplicants.map((app) => (
                        <tr key={app.id}>
                          <td className="mono-id">№ {app.id}</td>
                          <td>
                            <div className="app-name-block">
                              <strong>{app.fullName || app.full_name}</strong>
                              <span className="app-email">{app.email}</span>
                            </div>
                          </td>
                          <td className="phone-col">
                            <a href={`tel:${app.phoneNumber || app.phone_number || ''}`} className="admin-phone-pill" title="Qoʻngʻiroq qilish">
                              📞 {app.phoneNumber || app.phone_number || '—'}
                            </a>
                          </td>
                          <td className="mono">{app.passportNumber || app.passport_number || '—'}</td>
                          <td>
                            <span className="spec-table-tag">{app.specialization}</span>
                          </td>
                          <td>{app.otmName || app.otm_name || 'Turon universiteti'}</td>
                          <td>
                            <span className={`status-pill ${app.status}`}>
                              {app.status === 'approved' && '✓ Tasdiqlangan'}
                              {app.status === 'pending' && '⏳ Kutilmoqda'}
                              {app.status === 'rejected' && '✕ Rad etilgan'}
                              {app.status === 'passed' && '🎓 Oʻtgan'}
                            </span>
                          </td>
                          <td>
                            {app.testDate || app.test_date ? (
                              <div className="schedule-time-block">
                                <strong>📅 {app.testDate || app.test_date}</strong>
                                <span>⏰ {app.testTime || app.test_time || '10:00'}</span>
                                <small className="schedule-room-preview" title={app.examRoom || app.exam_room}>
                                  🏛️ {app.examRoom || app.exam_room || '214-auditoriya'}
                                </small>
                              </div>
                            ) : (
                              <span className="not-scheduled">Belgilanmagan</span>
                            )}
                          </td>
                          <td>
                            <div className="actions-cluster">
                              <button
                                className="btn-act view"
                                onClick={() => openModal(app, 'view')}
                                title="Talabgor anketasi va yuklangan hujjatlarni koʻrish"
                              >
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                              </button>

                              {app.status === 'pending' && (
                                <>
                                  <button
                                    className="btn-act approve"
                                    onClick={() => openModal(app, 'approve')}
                                    title="Tasdiqlash va imtihon sanasi/joyini belgilash"
                                  >
                                    ✓
                                  </button>
                                  <button
                                    className="btn-act reject"
                                    onClick={() => openModal(app, 'reject')}
                                    title="Rad qilish va sababini kiritish"
                                  >
                                    ✕
                                  </button>
                                </>
                              )}

                              {app.status === 'approved' && (
                                <button
                                  className="btn-act edit-schedule"
                                  onClick={() => openModal(app, 'approve')}
                                  title="Offline imtihon sanasi, vaqti va auditoriyasini tahrirlash"
                                >
                                  📅
                                </button>
                              )}

                              {(app.status === 'approved' || app.status === 'passed' || app.status === 'failed') && (
                                <button
                                  className="btn-act grade-result"
                                  onClick={() => openModal(app, 'result')}
                                  title="Offline imtihon natijasi va komissiya bayonnomasini kiritish"
                                  style={{ background: '#fef3c7', color: '#b45309', border: '1px solid #fde68a' }}
                                >
                                  🏆
                                </button>
                              )}

                              {/* Talabgorga tahrirlashga ruxsat berish */}
                              <button
                                className="btn-act edit-perm"
                                onClick={() => openModal(app, 'allow-edit')}
                                title={app.allowEdit ? "Tahrirlashga ruxsat berilgan (Batafsil / bekor qilish)" : "Talabgorga anketani tahrirlashga ruxsat berish"}
                                style={app.allowEdit ? { background: '#fef3c7', color: '#b45309', border: '1px solid #fde68a' } : {}}
                              >
                                ✏️
                              </button>

                              {app.status === 'rejected' && (
                                <button
                                  className="btn-act view-reason"
                                  onClick={() => openModal(app, 'reject')}
                                  title="Rad etish sababini koʻrish yoki oʻzgartirish"
                                >
                                  💬
                                </button>
                              )}

                              {/* Arizani serverdan butunlay oʻchirish */}
                              <button
                                className="btn-act delete-app"
                                onClick={() => handleDeleteApplicant(app.id, app.fullName || app.full_name)}
                                title="Arizani va unga tegishli barcha yuklangan hujjatlarni serverdan butunlay oʻchirish"
                                style={{ background: '#fee2e2', color: '#dc2626', border: '1px solid #fecaca' }}
                              >
                                🗑️
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}

        {/* 2-BOʻLIM: FOYDALANUVCHILAR BOSHQARUVI (LOGIN VA PAROLLAR) */}
        {mainTab === 'users' && (
          <div className="admin-users-management-view">
            {/* Users stats */}
            <div className="admin-stats-grid">
              <div className="stat-metric-card">
                <div className="stat-metric-icon blue">👥</div>
                <div className="stat-metric-data">
                  <span className="stat-number">{users.length}</span>
                  <span className="stat-label">Jami foydalanuvchilar</span>
                </div>
              </div>

              <div className="stat-metric-card">
                <div className="stat-metric-icon green">🎓</div>
                <div className="stat-metric-data">
                  <span className="stat-number">{users.filter(u => u.role === 'applicant').length}</span>
                  <span className="stat-label">Talabgorlar</span>
                </div>
              </div>

              <div className="stat-metric-card">
                <div className="stat-metric-icon purple">🛡️</div>
                <div className="stat-metric-data">
                  <span className="stat-number">{users.filter(u => u.role === 'admin').length}</span>
                  <span className="stat-label">Administratorlar</span>
                </div>
              </div>
            </div>

            {/* Users controls */}
            <div className="admin-table-controls">
              <div className="table-search-box">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
                <input
                  type="text"
                  placeholder="Foydalanuvchilarni qidirish (ism, login, email)..."
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                />
              </div>

              <div className="user-table-actions-row">
                <button
                  type="button"
                  className="btn-toggle-pass-visibility"
                  onClick={() => setShowAllPasswords(!showAllPasswords)}
                >
                  {showAllPasswords ? '🔒 Parollarni yashirish' : '👁️ Parollarni koʻrsatish'}
                </button>
                <button
                  type="button"
                  className="btn-add-user-modal-trigger"
                  onClick={openAddUserModal}
                >
                  + Yangi foydalanuvchi qoʻshish
                </button>
              </div>
            </div>

            {/* Users Table */}
            <div className="admin-table-wrapper">
              {userLoading ? (
                <div className="table-loading-box">
                  <div className="dash-spinner"></div>
                  <span>Foydalanuvchilar roʻyxati yuklanmoqda...</span>
                </div>
              ) : (
                <table className="admin-data-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Foydalanuvchi (F.I.Sh.)</th>
                      <th>Login / Elektron pochta</th>
                      <th>Parol (Tizimga kirish)</th>
                      <th>Rol</th>
                      <th>Roʻyxatdan oʻtgan sana</th>
                      <th style={{ textAlign: 'center' }}>Amallar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="empty-table-cell">
                          Foydalanuvchi topilmadi
                        </td>
                      </tr>
                    ) : (
                      filteredUsers.map((u) => (
                        <tr key={u.id}>
                          <td className="mono-id">#{u.id}</td>
                          <td>
                            <strong>{u.name}</strong>
                          </td>
                          <td>
                            <code className="user-login-badge">{u.email}</code>
                          </td>
                          <td>
                            <span className="user-password-code" title="Foydalanuvchining asl paroli">
                              {showAllPasswords ? u.password : '••••••••'}
                            </span>
                          </td>
                          <td>
                            <span className={`user-role-chip ${u.role === 'admin' ? 'admin' : 'applicant'}`}>
                              {u.role === 'admin' ? '🛡️ Administrator' : '🎓 Talabgor'}
                            </span>
                          </td>
                          <td>
                            <span className="user-date-text">
                              {u.created_at ? new Date(u.created_at).toLocaleDateString('uz-UZ') : '10.10.2026'}
                            </span>
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <div className="user-actions-cluster">
                              <button
                                className="btn-user-action edit"
                                onClick={() => openEditUserModal(u)}
                                title="Login va parolni tahrirlash"
                              >
                                ✏️ Tahrirlash
                              </button>
                              {u.id !== 999 && (
                                <button
                                  className="btn-user-action delete"
                                  onClick={() => handleDeleteUser(u.id, u.name)}
                                  title="Foydalanuvchini oʻchirish"
                                >
                                  🗑️ Oʻchirish
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {/* 3-BOʻLIM: XIZMATLAR VA MUDDATLAR (DEADLINE) BOSHQARUVI */}
        {mainTab === 'services' && (
          <div className="admin-services-management-view">
            <div className="services-admin-header-strip">
              <div>
                <h3 className="services-admin-title">🏛️ Universitet xizmatlari va qabul muddatlari (Deadline) sozlamalari</h3>
                <p className="services-admin-subtitle">
                  Har bir xizmat boʻyicha hujjat topshirishning oxirgi muddati (deadline taymeri), qabul davri, ish vaqti hamda xizmat talablarini real vaqtda boshqaring.
                </p>
              </div>
              <button
                className="btn-admin-action"
                onClick={loadServices}
                disabled={servicesLoading}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 4 23 10 17 10"></polyline><polyline points="1 20 1 14 7 14"></polyline><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path></svg>
                <span>Xizmatlarni yangilash</span>
              </button>
            </div>

            {servicesLoading ? (
              <div className="admin-table-loading">Xizmatlar maʼlumotlari yuklanmoqda...</div>
            ) : (
              <div className="admin-services-cards-grid">
                {services.map((svc) => (
                  <div key={svc.id} className="admin-service-config-card">
                    <div className="admin-service-card-header">
                      <div>
                        <span className="svc-cat-tag">{svc.category || 'Boʻlim'}</span>
                        <h4 className="svc-card-title">{svc.title}</h4>
                      </div>
                      {(() => {
                        const isExp = svc.deadline ? (new Date(svc.deadline).getTime() <= Date.now()) : false;
                        const isAct = svc.status === 'active' && !isExp;
                        return (
                          <span className={`svc-status-badge ${isAct ? 'active' : 'closed'}`}>
                            {isAct ? '● Qabul ochiq (Faol)' : isExp ? '✕ Qabul muddati tugagan (Nofaol)' : '✕ Qabul yopilgan'}
                          </span>
                        );
                      })()}
                    </div>

                    <div className="admin-service-card-body">
                      {(() => {
                        const isExp = svc.deadline ? (new Date(svc.deadline).getTime() <= Date.now()) : false;
                        return (
                          <div className={`svc-info-row highlight-box ${isExp ? 'is-expired-box' : ''}`}>
                            <span className="svc-info-lbl">⏳ Oxirgi muddat (Deadline):</span>
                            <span className={`svc-info-val highlight ${isExp ? 'expired-text' : ''}`}>
                              {svc.deadline
                                ? new Date(svc.deadline).toLocaleString('uz-UZ', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                                : 'Belgilanmagan'}
                              {isExp && <span className="expired-pill-badge"> (Muddati oʻtgan)</span>}
                            </span>
                          </div>
                        );
                      })()}
                      <div className="svc-info-row">
                        <span className="svc-info-lbl">📅 Qabul davri:</span>
                        <span className="svc-info-val">{svc.receptionPeriod || 'Doimiy'}</span>
                      </div>
                      <div className="svc-info-row">
                        <span className="svc-info-lbl">★ Xizmat reytingi:</span>
                        <span className="svc-info-val highlight" style={{ color: '#d97706' }}>
                          ★ {svc.rating ? Number(svc.rating).toFixed(1) : '5.0'} / 5.0
                          <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 500, marginLeft: '4px' }}>
                            ({svc.reviewCount || 0} ta sharh)
                          </span>
                        </span>
                      </div>
                      <div className="svc-info-row">
                        <span className="svc-info-lbl">🕒 Ish / qabul vaqti:</span>
                        <span className="svc-info-val">{svc.workingHours || '09:00 – 17:00'}</span>
                      </div>
                      <div className="svc-info-row">
                        <span className="svc-info-lbl">⏱ Ariza toʻldirish:</span>
                        <span className="svc-info-val">{svc.timeToApply || 8} daqiqa</span>
                      </div>
                      <div className="svc-info-row">
                        <span className="svc-info-lbl">💰 Xizmat narxi:</span>
                        <span className="svc-info-val">{svc.priceText || 'Bepul'}</span>
                      </div>
                      <div className="svc-info-row full-width">
                        <span className="svc-info-lbl">📋 Xizmat talablari:</span>
                        <span className="svc-info-val desc">{svc.requirements || 'Belgilanmagan'}</span>
                      </div>
                    </div>

                    <div className="admin-service-card-footer">
                      <div className="svc-counts-pill">
                        <span>👥 Foydalanuvchilar: <strong>{svc.applicantCount || 0} nafar</strong></span>
                      </div>
                      <button
                        type="button"
                        className="btn-edit-service"
                        onClick={() => openEditServiceModal(svc)}
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
                        <span>Muddat va talablarni tahrirlash</span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* 4-BOʻLIM: XIZMAT SHARHLARI VA BAHOLASH MONITORINGI */}
        {mainTab === 'reviews' && (
          <div className="admin-reviews-management-view">
            {/* Stats row for reviews */}
            <div className="admin-stats-grid">
              <div className="stat-metric-card">
                <div className="stat-metric-icon blue">💬</div>
                <div className="stat-metric-data">
                  <span className="stat-number">{reviews.length}</span>
                  <span className="stat-label">Jami sharhlar soni</span>
                </div>
              </div>

              <div className="stat-metric-card">
                <div className="stat-metric-icon amber">⭐</div>
                <div className="stat-metric-data">
                  <span className="stat-number">
                    {reviews.length > 0 
                      ? (reviews.reduce((acc, r) => acc + (Number(r.rating) || 5), 0) / reviews.length).toFixed(1)
                      : '5.0'}
                  </span>
                  <span className="stat-label">Oʻrtacha umumiy baho</span>
                </div>
              </div>

              <div className="stat-metric-card">
                <div className="stat-metric-icon green">★</div>
                <div className="stat-metric-data">
                  <span className="stat-number">
                    {reviews.filter(r => Number(r.rating) === 5).length}
                  </span>
                  <span className="stat-label">5 yulduzli aʼlo baholar</span>
                </div>
              </div>

              <div className="stat-metric-card">
                <div className="stat-metric-icon purple">🔄</div>
                <div className="stat-metric-data">
                  <span className="stat-number" style={{ fontSize: '18px' }}>Avtomatik</span>
                  <span className="stat-label">100% Sinxron reyting</span>
                </div>
              </div>
            </div>

            {/* Dynamic Rating Explanation Banner */}
            <div className="review-system-banner">
              <div className="review-system-banner-icon">💡</div>
              <div className="review-system-banner-text">
                <strong>Avtomatik Dinamik Baho Qoidasi:</strong> Har qanday xizmat sharhi tahrirlanganda yoki oʻchirilganda, ushbu xizmatning umumiy bahosi (★) unga berilgan baholarning oʻrtacha arifmetik qiymati asosida darhol qayta hisoblanadi va butun sayt (Bosh sahifa, Xizmat sahifasi, Admin panel) boʻylab sinxron aks etadi.
              </div>
            </div>

            {/* Filter and Action Bar */}
            <div className="review-admin-filter-bar">
              <div className="review-filter-group">
                <label style={{ fontSize: '13.5px', fontWeight: 600, color: '#334155' }}>Xizmat boʻyicha filter:</label>
                <select
                  className="review-filter-select"
                  value={selectedReviewService}
                  onChange={(e) => {
                    const sId = e.target.value;
                    setSelectedReviewService(sId);
                    loadReviews(sId);
                  }}
                >
                  <option value="all">Barcha xizmatlar ({reviews.length})</option>
                  {services.map(s => (
                    <option key={s.id} value={s.id}>
                      {s.title} (★ {s.rating || 5.0})
                    </option>
                  ))}
                </select>
              </div>

              <button
                type="button"
                className="btn-add-review-admin"
                onClick={() => openAddReviewModal(selectedReviewService)}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                <span>Yangi sharh qoʻshish</span>
              </button>
            </div>

            {/* Reviews Grid */}
            {reviewLoading ? (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>Sharhlar yuklanmoqda...</p>
              </div>
            ) : reviews.length === 0 ? (
              <div className="empty-state-box">
                <p>Ushbu xizmat boʻyicha hozircha sharhlar mavjud emas.</p>
                <button
                  type="button"
                  className="btn-add-review-admin"
                  style={{ marginTop: '12px' }}
                  onClick={() => openAddReviewModal(selectedReviewService)}
                >
                  + Birinchi sharhni qoʻshish
                </button>
              </div>
            ) : (
              <div className="reviews-admin-grid">
                {reviews.map(rev => (
                  <div key={rev.id} className="review-admin-card">
                    <div className="review-admin-card-top">
                      <div className="review-admin-author">
                        <div
                          className="review-author-avatar"
                          style={{ background: rev.avatarBg || '#dbeafe', color: rev.avatarColor || '#2563eb' }}
                        >
                          {rev.userInitials || 'TL'}
                        </div>
                        <div className="review-author-meta">
                          <span className="review-author-name">{rev.userName}</span>
                          <span className="review-service-badge" title={rev.serviceTitle}>
                            {rev.serviceTitle || rev.serviceId}
                          </span>
                        </div>
                      </div>

                      <div className="review-stars-pill">
                        <span>★</span>
                        <span>{rev.rating}.0</span>
                      </div>
                    </div>

                    <div className="review-admin-comment">
                      "{rev.comment}"
                    </div>

                    <div className="review-admin-footer">
                      <span className="review-admin-date">
                        🕒 {rev.timeAgo || new Date(rev.createdAt).toLocaleDateString('uz-UZ')}
                      </span>
                      <div className="review-admin-actions">
                        <button
                          type="button"
                          className="btn-rev-act edit"
                          onClick={() => openEditReviewModal(rev)}
                          title="Tahrirlash"
                        >
                          ✏️ Tahrirlash
                        </button>
                        <button
                          type="button"
                          className="btn-rev-act delete"
                          onClick={() => handleDeleteReview(rev.serviceId, rev.id)}
                          title="Oʻchirish"
                        >
                          🗑️ Oʻchirish
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* SERVICE EDIT MODAL */}
      {editingService && (
        <div className="admin-modal-overlay" onClick={() => setEditingService(null)}>
          <div className="admin-modal-box large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-top">
              <h3>«{editingService.title}» — Muddat va talablarni sozlash</h3>
              <button
                type="button"
                className="btn-close-modal"
                onClick={() => setEditingService(null)}
                title="Yopish (Esc)"
              >
                ✕
              </button>
            </div>

            {serviceSaveMsg && (
              <div className="service-modal-success-box" style={{ background: '#ecfdf5', color: '#065f46', border: '1px solid #a7f3d0', padding: '10px 14px', borderRadius: '8px', marginBottom: '16px', fontWeight: 600 }}>
                ✓ {serviceSaveMsg}
              </div>
            )}

            <form onSubmit={handleSaveService} className="modal-service-form">
              <div className="modal-form-grid">
                <div className="form-group-modal full-width">
                  <label>⏳ Hujjat topshirishning oxirgi muddati (Deadline):</label>
                  <input
                    type="datetime-local"
                    value={serviceForm.deadline}
                    onChange={(e) => handleDeadlineChange(e.target.value)}
                    required
                  />

                  {/* Jonli muddat holati koʻrsatkichi */}
                  {(() => {
                    if (!serviceForm.deadline) return null;
                    const d = new Date(serviceForm.deadline);
                    if (isNaN(d.getTime())) return null;
                    const diff = d.getTime() - Date.now();
                    const isPast = diff <= 0;
                    const daysLeft = Math.floor(diff / (1000 * 60 * 60 * 24));
                    const hoursLeft = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                    const pad = (n) => String(n).padStart(2, '0');
                    const dStr = `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;

                    if (isPast) {
                      const passedDays = Math.ceil(Math.abs(diff) / (1000 * 60 * 60 * 24));
                      return (
                        <div className="deadline-status-alert alert-past">
                          <div className="alert-icon">⚠️</div>
                          <div className="alert-body">
                            <div className="alert-title">Diqqat: Tanlangan sana oʻtib ketgan ({dStr})!</div>
                            <div className="alert-desc">
                              Ushbu muddat {passedDays} kun avval yakunlangan. Agar shunday saqlasangiz, saytda <strong>«Qabul muddati yakunlangan»</strong> deb koʻrinadi va <strong>«Xizmatdan foydalanish»</strong> tugmasi nofaol (bloklangan) boʻlib qoladi. Qabulni ochish uchun bugundan keyingi sanani belgilang yoki quyidagi tezkor tugmalardan foydalaning.
                            </div>
                          </div>
                        </div>
                      );
                    }

                    return (
                      <div className="deadline-status-alert alert-future">
                        <div className="alert-icon">✅</div>
                        <div className="alert-body">
                          <div className="alert-title">Qabul ochiq (Faol muddat: {dStr})</div>
                          <div className="alert-desc">
                            Muddat tugashiga <strong>{daysLeft} kun, {hoursLeft} soat</strong> qoldi. Foydalanuvchilarga saytda jonli taymer orqaga sanaydi va <strong>«Xizmatdan foydalanish»</strong> tugmasi toʻliq <strong>FAOL (koʻk)</strong> boʻladi.
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Tezkor muddat belgilash tugmalari */}
                  <div className="deadline-quick-presets">
                    <span className="presets-lbl">⚡ Tezkor uzaytirish:</span>
                    <div className="presets-btns-row">
                      <button type="button" className="btn-preset" onClick={() => applyDeadlinePreset(7)}>+7 kun</button>
                      <button type="button" className="btn-preset" onClick={() => applyDeadlinePreset(15)}>+15 kun</button>
                      <button type="button" className="btn-preset highlight" onClick={() => applyDeadlinePreset(30)}>+1 oy (30 kun)</button>
                      <button type="button" className="btn-preset" onClick={() => applyDeadlinePreset(null, '2026-11-30T23:59:00')}>30-noyabrgacha</button>
                      <button type="button" className="btn-preset" onClick={() => applyDeadlinePreset(null, '2026-12-31T23:59:00')}>31-dekabrgacha</button>
                    </div>
                  </div>
                </div>

                <div className="form-group-modal full-width">
                  <label>📅 Qabul davri (Sanalar matni):</label>
                  <input
                    type="text"
                    placeholder="Masalan: 2026-yil 5-oktabrdan 30-noyabrgacha"
                    value={serviceForm.receptionPeriod}
                    onChange={(e) => setServiceForm({ ...serviceForm, receptionPeriod: e.target.value })}
                    required
                  />
                  <span className="input-field-tip">Oxirgi muddat oʻzgarganda avtomatik yangilanadi yoki qoʻlda tuzatishingiz mumkin.</span>
                </div>

                <div className="form-group-modal">
                  <label>Boshlanish sanasi (Format: 05.10.2026):</label>
                  <input
                    type="text"
                    value={serviceForm.receptionStart}
                    onChange={(e) => setServiceForm({ ...serviceForm, receptionStart: e.target.value })}
                  />
                </div>

                <div className="form-group-modal">
                  <label>Tugash sanasi (Format: 30.11.2026):</label>
                  <input
                    type="text"
                    value={serviceForm.receptionEnd}
                    onChange={(e) => handleReceptionEndChange(e.target.value)}
                  />
                </div>

                <div className="form-group-modal">
                  <label>🕒 Ish / qabul vaqti:</label>
                  <input
                    type="text"
                    placeholder="09:00 – 17:00 (Dushanba – Juma)"
                    value={serviceForm.workingHours}
                    onChange={(e) => setServiceForm({ ...serviceForm, workingHours: e.target.value })}
                  />
                </div>

                <div className="form-group-modal">
                  <label>⏱ Koʻrib chiqish muddati:</label>
                  <input
                    type="text"
                    placeholder="Masalan: 30 kun"
                    value={serviceForm.processingDays}
                    onChange={(e) => setServiceForm({ ...serviceForm, processingDays: e.target.value })}
                  />
                </div>

                <div className="form-group-modal">
                  <label>Ariza toʻldirish vaqti (daqiqa):</label>
                  <input
                    type="number"
                    min="1"
                    max="60"
                    value={serviceForm.timeToApply}
                    onChange={(e) => setServiceForm({ ...serviceForm, timeToApply: e.target.value })}
                  />
                </div>

                <div className="form-group-modal">
                  <label>Xizmat holati:</label>
                  <select
                    value={serviceForm.status}
                    onChange={(e) => setServiceForm({ ...serviceForm, status: e.target.value })}
                  >
                    <option value="active">Qabul ochiq (Faol)</option>
                    <option value="closed">Qabul toʻxtatilgan (Yopiq)</option>
                  </select>
                </div>

                <div className="form-group-modal full-width">
                  <label>📋 Xizmat talablari va tartibi:</label>
                  <textarea
                    rows="3"
                    placeholder="OAK Nizomi talablari, zarur hujjatlar..."
                    value={serviceForm.requirements}
                    onChange={(e) => setServiceForm({ ...serviceForm, requirements: e.target.value })}
                  ></textarea>
                </div>

                <div className="form-group-modal full-width">
                  <label>💰 Xizmat narxi va toʻlov shartlari:</label>
                  <textarea
                    rows="2"
                    placeholder="Masalan: BHMning 50% miqdorida..."
                    value={serviceForm.priceText}
                    onChange={(e) => setServiceForm({ ...serviceForm, priceText: e.target.value })}
                  ></textarea>
                </div>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-modal-cancel"
                  onClick={() => setEditingService(null)}
                >
                  Bekor qilish
                </button>
                <button
                  type="submit"
                  className="btn-modal-save"
                  disabled={actionLoading}
                >
                  {actionLoading ? 'Saqlanmoqda...' : 'Saqlash va saytda yangilash'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* USER ADD / EDIT MODAL */}
      {userModal && (
        <div className="admin-modal-overlay" onClick={() => setUserModal(null)}>
          <div className="admin-modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="modal-top">
              <h3>
                {userModal === 'add' ? 'Yangi foydalanuvchi qoʻshish' : 'Foydalanuvchi maʼlumotlari va parolini tahrirlash'}
              </h3>
              <button
                type="button"
                className="btn-close-modal"
                onClick={() => setUserModal(null)}
                title="Yopish (Esc)"
              >
                ✕
              </button>
            </div>

            {userFormError && (
              <div className="user-modal-error-box">
                ⚠️ {userFormError}
              </div>
            )}

            <form onSubmit={handleSaveUser} className="modal-user-form">
              <div className="form-group-modal">
                <label>F.I.Sh. (Toʻliq ism sharifi):</label>
                <input
                  type="text"
                  placeholder="Masalan: Karimova Shahlo Erkinovna"
                  value={userForm.name}
                  onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                  required
                />
              </div>

              <div className="form-group-modal">
                <label>Login / Elektron pochta manzili:</label>
                <input
                  type="text"
                  placeholder="Masalan: shahlo.karimova@turon.uz"
                  value={userForm.email}
                  onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                  required
                />
              </div>

              <div className="form-group-modal">
                <label>Parol (Login uchun):</label>
                <input
                  type="text"
                  placeholder="Yangi parol kiriting"
                  value={userForm.password}
                  onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                  required
                />
                <span className="input-field-tip">
                  Foydalanuvchi ushbu login va parol bilan tizimga kirishi mumkin.
                </span>
              </div>

              <div className="form-group-modal">
                <label>Tizimdagi roli:</label>
                <select
                  value={userForm.role}
                  onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
                >
                  <option value="applicant">Talabgor (Oddiy foydalanuvchi)</option>
                  <option value="admin">Administrator (Tizim boshqaruvchisi)</option>
                </select>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-modal-cancel"
                  onClick={() => setUserModal(null)}
                >
                  Bekor qilish
                </button>
                <button
                  type="submit"
                  className="btn-modal-save"
                  disabled={actionLoading}
                >
                  {actionLoading ? 'Saqlanmoqda...' : 'Saqlash'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* APPROVAL MODAL */}
      {modalType === 'approve' && selectedApp && (
        <div className="admin-modal-overlay" onClick={() => setModalType(null)}>
          <div
            className="admin-modal-box"
            onClick={(e) => e.stopPropagation()}
            style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
          >
            <div className="modal-top" style={{ flexShrink: 0, marginBottom: '14px', paddingBottom: '12px', borderBottom: '1px solid #e2e8f0' }}>
              <h3 style={{ color: '#0f2b5c', margin: 0, fontSize: '17px', fontWeight: '700' }}>
                Arizani tasdiqlash va imtihon tayinlash
              </h3>
              <button
                type="button"
                className="btn-close-modal"
                onClick={() => setModalType(null)}
                title="Yopish (Esc)"
                aria-label="Yopish"
              >
                ✕
              </button>
            </div>

            <div
              className="admin-modal-scrollable-body"
              style={{
                overflowY: 'auto',
                flex: 1,
                minHeight: 0,
                paddingRight: '6px',
                display: 'flex',
                flexDirection: 'column',
                gap: '14px'
              }}
            >
              <div className="modal-app-summary" style={{ margin: 0, flexShrink: 0 }}>
                <strong>{selectedApp.fullName || selectedApp.full_name}</strong>
                <span>Fan: {selectedApp.specialization}</span>
              </div>

              <div className="modal-form-grid" style={{ margin: 0 }}>
                <div className="form-group-modal">
                  <label>Imtihon sanasi:</label>
                  <input
                    type="date"
                    value={testDate}
                    onChange={(e) => setTestDate(e.target.value)}
                  />
                </div>

                <div className="form-group-modal">
                  <label>Boshlanish vaqti:</label>
                  <input
                    type="time"
                    value={testTime}
                    onChange={(e) => setTestTime(e.target.value)}
                  />
                </div>

                <div className="form-group-modal full-width">
                  <label>Offline imtihon oʻtkaziladigan bino va auditoriya (Joyi):</label>
                  <input
                    type="text"
                    placeholder="Masalan: Turon universiteti bosh binosi, 214-auditoriya (Qarshi shahri, Nasaf koʻchasi, 1/14-uy)"
                    value={examRoom}
                    onChange={(e) => setExamRoom(e.target.value)}
                  />
                </div>

                <div className="form-group-modal full-width">
                  <label>Talabgor uchun rasmiy yoʻriqnoma va eslatma (Offline qoidalar):</label>
                  <textarea
                    rows="3"
                    placeholder="Talabgor shaxsiy kabinetida koʻrinadigan yoʻriqnoma..."
                    value={examNotes}
                    onChange={(e) => setExamNotes(e.target.value)}
                  ></textarea>
                </div>
              </div>
            </div>

            <div
              className="modal-actions"
              style={{
                flexShrink: 0,
                marginTop: '14px',
                paddingTop: '14px',
                borderTop: '1px solid #e2e8f0',
                background: '#ffffff',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '12px'
              }}
            >
              <button className="btn-modal-cancel" onClick={() => setModalType(null)}>Bekor qilish</button>
              <button className="btn-modal-save" onClick={handleApproveSubmit} disabled={actionLoading}>
                {actionLoading ? 'Saqlanmoqda...' : 'Tasdiqlash va offline yoʻllanma berish'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* REJECTION MODAL */}
      {modalType === 'reject' && selectedApp && (
        <div className="admin-modal-overlay" onClick={() => setModalType(null)}>
          <div
            className="admin-modal-box"
            onClick={(e) => e.stopPropagation()}
            style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
          >
            <div className="modal-top" style={{ flexShrink: 0, marginBottom: '14px', paddingBottom: '12px', borderBottom: '1px solid #e2e8f0' }}>
              <h3 className="text-red" style={{ margin: 0, fontSize: '17px', fontWeight: '700' }}>
                Arizani rad qilish
              </h3>
              <button
                type="button"
                className="btn-close-modal"
                onClick={() => setModalType(null)}
                title="Yopish (Esc)"
                aria-label="Yopish"
              >
                ✕
              </button>
            </div>

            <div
              className="admin-modal-scrollable-body"
              style={{
                overflowY: 'auto',
                flex: 1,
                minHeight: 0,
                paddingRight: '6px',
                display: 'flex',
                flexDirection: 'column',
                gap: '14px'
              }}
            >
              <div className="modal-app-summary" style={{ margin: 0, flexShrink: 0 }}>
                <strong>{selectedApp.fullName || selectedApp.full_name}</strong>
                <span>Fan: {selectedApp.specialization} • Tel: {selectedApp.phoneNumber || selectedApp.phone_number || '—'}</span>
              </div>

              <div className="form-group-modal">
                <label>Rad qilish sababini koʻrsating (Talabgorga koʻrinadi):</label>
                <div className="quick-reasons-row">
                  <button type="button" onClick={() => setRejectReason('OAK Byulletenidagi dissertatsiya mavzusi tasdiqlangan nusxasi toʻliq yuklanmagan.')}>
                    OAK nusxasi yetarli emas
                  </button>
                  <button type="button" onClick={() => setRejectReason('Boshqa OTMdan topshirilgan ariza uchun belgilangan toʻlov kvitansiyasi biriktirilmagan.')}>
                    Toʻlov kvitansiyasi yoʻq
                  </button>
                  <button type="button" onClick={() => setRejectReason('Pasport nusxasi sifatli yuklanmagan yoki muddati oʻtgan.')}>
                    Pasport noaniq
                  </button>
                  <button type="button" onClick={() => setRejectReason('Taqdim etilgan doktorantura maʼlumotnomasi talabga javob bermaydi.')}>
                    Maʼlumotnoma notoʻgʻri
                  </button>
                </div>
                <textarea
                  rows="3"
                  placeholder="Talabgor shaxsiy kabinetida koʻrsatiladigan rasmiy sabab..."
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                ></textarea>
              </div>
            </div>

            <div
              className="modal-actions"
              style={{
                flexShrink: 0,
                marginTop: '14px',
                paddingTop: '14px',
                borderTop: '1px solid #e2e8f0',
                background: '#ffffff',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '12px'
              }}
            >
              <button className="btn-modal-cancel" onClick={() => setModalType(null)}>Bekor qilish</button>
              <button className="btn-modal-danger" onClick={handleRejectSubmit} disabled={actionLoading}>
                {actionLoading ? 'Yuborilmoqda...' : 'Arizani rad qilish'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ALLOW EDIT MODAL */}
      {modalType === 'allow-edit' && selectedApp && (
        <div className="admin-modal-overlay" onClick={() => setModalType(null)}>
          <div
            className="admin-modal-box"
            onClick={(e) => e.stopPropagation()}
            style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
          >
            <div className="modal-top" style={{ flexShrink: 0, marginBottom: '14px', paddingBottom: '12px', borderBottom: '1px solid #e2e8f0' }}>
              <h3 style={{ color: '#0f2b5c', margin: 0, fontSize: '17px', fontWeight: '700' }}>
                ✏️ Talabgorga arizani tahrirlashga ruxsat berish
              </h3>
              <button
                type="button"
                className="btn-close-modal"
                onClick={() => setModalType(null)}
                title="Yopish (Esc)"
              >
                ✕
              </button>
            </div>

            <div
              className="admin-modal-scrollable-body"
              style={{
                overflowY: 'auto',
                flex: 1,
                minHeight: 0,
                paddingRight: '6px',
                display: 'flex',
                flexDirection: 'column',
                gap: '14px'
              }}
            >
              <div className="modal-app-summary" style={{ margin: 0, flexShrink: 0 }}>
                <strong>{selectedApp.fullName || selectedApp.full_name}</strong>
                <span>Fan: {selectedApp.specialization} • Tel: {selectedApp.phoneNumber || selectedApp.phone_number || '—'}</span>
              </div>

              {selectedApp.allowEdit && (
                <div style={{ background: '#fef3c7', padding: '12px', borderRadius: '8px', border: '1px solid #fde68a', color: '#92400e', fontSize: '13.5px' }}>
                  ℹ️ <strong>Hozirda tahrirlash ruxsati faol.</strong> Talabgor oʻz shaxsiy kabinetida anketasini yoki yuklagan hujjatlarini oʻzgartirishi mumkin.
                </div>
              )}

              <div className="form-group-modal">
                <label>Tahrirlash sababi / Talabgor uchun koʻrsatma:</label>
                <div className="quick-reasons-row">
                  <button type="button" onClick={() => setEditReason('Yuklangan hujjatlar sifati yetarli emas. Iltimos, aniqroq qilib qayta yuklang.')}>
                    Hujjatlar sifati yetarli emas
                  </button>
                  <button type="button" onClick={() => setEditReason('Pasport / ID-karta nusxasi noaniq yoki muddati oʻtgan.')}>
                    Pasport noaniq
                  </button>
                  <button type="button" onClick={() => setEditReason('OAK byulleteni koʻchirmasi yoki maʼlumotnoma toʻliq yuklanmagan.')}>
                    OAK koʻchirmasi toʻliq emas
                  </button>
                  <button type="button" onClick={() => setEditReason('Anketa maʼlumotlarida (ilmiy rahbar yoki mavzuda) xatolik mavjud.')}>
                    Mavzu yoki rahbar noaniq
                  </button>
                </div>
                <textarea
                  rows="3"
                  placeholder="Talabgor shaxsiy kabinetida koʻrinadigan koʻrsatma..."
                  value={editReason}
                  onChange={(e) => setEditReason(e.target.value)}
                ></textarea>
                <small style={{ color: '#64748b', fontSize: '12px', marginTop: '4px' }}>
                  Talabgor ushbu koʻrsatma asosida oʻz anketasini toʻgʻrilab yoki fayllarini qayta yuklab, arizasini yangilaydi.
                </small>
              </div>
            </div>

            <div
              className="modal-actions"
              style={{
                flexShrink: 0,
                marginTop: '14px',
                paddingTop: '14px',
                borderTop: '1px solid #e2e8f0',
                background: '#ffffff',
                display: 'flex',
                justifyContent: 'space-between',
                gap: '12px'
              }}
            >
              <div>
                {selectedApp.allowEdit && (
                  <button
                    type="button"
                    className="btn-modal-cancel"
                    style={{ color: '#dc2626', borderColor: '#fca5a5' }}
                    onClick={() => handleRevokeEdit(selectedApp.id)}
                    disabled={actionLoading}
                  >
                    Ruxsatni bekor qilish
                  </button>
                )}
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button className="btn-modal-cancel" onClick={() => setModalType(null)}>Bekor qilish</button>
                <button className="btn-modal-save" onClick={handleAllowEditSubmit} disabled={actionLoading}>
                  {actionLoading ? 'Saqlanmoqda...' : 'Tahrirlashga ruxsat berish'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* VIEW DETAILS & DOCUMENTS MODAL */}
      {modalType === 'view' && selectedApp && (
        <div className="admin-modal-overlay" onClick={() => setModalType(null)}>
          <div
            className="admin-modal-box large"
            onClick={(e) => e.stopPropagation()}
            style={{ maxHeight: '92vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
          >
            <div className="modal-top">
              <h3>Talabgorning toʻliq anketasi va hujjatlari</h3>
              <button
                type="button"
                className="btn-close-modal"
                onClick={() => setModalType(null)}
                title="Yopish (Esc)"
              >
                ✕
              </button>
            </div>

            <div className="admin-modal-scrollable-body" style={{ overflowY: 'auto', flex: 1, minHeight: 0, paddingRight: '10px' }}>
              <div className="view-details-content">
                <div className="vd-row">
                  <span className="vd-lbl">Ariza raqami:</span>
                  <span className="vd-val"><strong>№ {selectedApp.id}</strong></span>
                </div>
                <div className="vd-row">
                  <span className="vd-lbl">F.I.Sh.:</span>
                  <span className="vd-val">{selectedApp.fullName || selectedApp.full_name}</span>
                </div>
                <div className="vd-row">
                  <span className="vd-lbl">Telefon raqami:</span>
                  <span className="vd-val highlight">
                    <a href={`tel:${selectedApp.phoneNumber || selectedApp.phone_number}`} style={{ color: '#0284c7', textDecoration: 'none' }}>
                      📞 {selectedApp.phoneNumber || selectedApp.phone_number || '—'}
                    </a>
                  </span>
                </div>
                <div className="vd-row">
                  <span className="vd-lbl">Elektron pochta:</span>
                  <span className="vd-val">{selectedApp.email}</span>
                </div>
                <div className="vd-row">
                  <span className="vd-lbl">Tugʻilgan sana:</span>
                  <span className="vd-val">{selectedApp.birthDate || selectedApp.birth_date || '—'}</span>
                </div>
                <div className="vd-row">
                  <span className="vd-lbl">JSHSHIR raqami:</span>
                  <span className="vd-val mono">{selectedApp.passportNumber || selectedApp.passport_number}</span>
                </div>
                <div className="vd-row">
                  <span className="vd-lbl">OTM / ITM:</span>
                  <span className="vd-val">{selectedApp.otmName || selectedApp.otm_name}</span>
                </div>
                <div className="vd-row">
                  <span className="vd-lbl">Fan nomi / Ixtisoslik:</span>
                  <span className="vd-val highlight">{selectedApp.specialization}</span>
                </div>
                <div className="vd-row">
                  <span className="vd-lbl">Dissertatsiya mavzusi:</span>
                  <span className="vd-val">{selectedApp.dissertationTopic || 'Mavzu kiritilgan'}</span>
                </div>
                <div className="vd-row">
                  <span className="vd-lbl">Ilmiy rahbar / Maslahatchi:</span>
                  <span className="vd-val">{selectedApp.advisorInfo || 'Mavjud'}</span>
                </div>
                <div className="vd-row">
                  <span className="vd-lbl">Ariza holati:</span>
                  <span className="vd-val">
                    <span className={`status-pill ${selectedApp.status}`}>
                      {selectedApp.status === 'approved' && '✓ Tasdiqlangan'}
                      {selectedApp.status === 'pending' && '⏳ Kutilmoqda'}
                      {selectedApp.status === 'rejected' && '✕ Rad etilgan'}
                      {selectedApp.status === 'passed' && '🎓 Imtihondan oʻtgan'}
                      {selectedApp.status === 'failed' && '⚠️ Oʻta olmagan'}
                    </span>
                  </span>
                </div>
                {selectedApp.rejectionReason && (
                  <div className="vd-row" style={{ background: '#fef2f2', padding: '10px 14px', borderRadius: '8px' }}>
                    <span className="vd-lbl" style={{ color: '#dc2626' }}>Rad etish sababi:</span>
                    <span className="vd-val" style={{ color: '#b91c1c' }}>{selectedApp.rejectionReason}</span>
                  </div>
                )}
                {selectedApp.status === 'approved' && (
                  <div className="vd-row" style={{ background: '#ecfdf5', padding: '10px 14px', borderRadius: '8px' }}>
                    <span className="vd-lbl" style={{ color: '#059669' }}>Offline imtihon:</span>
                    <span className="vd-val" style={{ color: '#047857' }}>
                      📅 {selectedApp.testDate} soat {selectedApp.testTime} | 🏛️ {selectedApp.examRoom}
                    </span>
                  </div>
                )}
                {selectedApp.examResult && (
                  <div className="vd-row" style={{ background: '#fffbeb', padding: '10px 14px', borderRadius: '8px', border: '1px solid #fde68a' }}>
                    <span className="vd-lbl" style={{ color: '#b45309' }}>Offline imtihon natijasi:</span>
                    <span className="vd-val" style={{ color: '#92400e' }}>
                      <strong>{selectedApp.examResult.score} ball ({selectedApp.examResult.status === 'passed' ? '✓ Oʻtgan' : '✕ Oʻtmagan'})</strong>
                      {selectedApp.examResult.certificateNumber && ` | Qayd: ${selectedApp.examResult.certificateNumber}`}
                    </span>
                  </div>
                )}
              </div>

              {/* INTERACTIVE APPLICANT DOCUMENTS LIST */}
              <div className="vd-docs-list">
                <h4>Yuklangan rasmiy hujjatlar (Admin tekshiruvi):</h4>
                <div className="vd-docs-cards-list">
                  {[
                    { key: 'application', title: 'Malakaviy imtihon arizasi', icon: '📝', defaultFile: `ariza_${selectedApp.id}.pdf` },
                    { key: 'oakBulletin', title: 'OAK Byulleteni koʻchirmasi', icon: '📰', defaultFile: `oak_bulletin_${selectedApp.id}.pdf` },
                    { key: 'phd_certificate', title: 'Doktorantura maʼlumotnomasi', icon: '🏛️', defaultFile: `doktorantura_guvohnoma_${selectedApp.id}.pdf` },
                    { key: 'passport', title: 'Pasport / ID-karta nusxasi', icon: '🪪', defaultFile: `passport_${selectedApp.id}.pdf` },
                    ...(selectedApp.applicantType !== 'Ushbu taʼlim muassasasi' || (selectedApp.documents && selectedApp.documents.paymentReceipt) ? [
                      { key: 'paymentReceipt', title: 'Toʻlov kvitansiyasi (BHM 50%)', icon: '💳', defaultFile: `tolov_kvitansiyasi_${selectedApp.id}.pdf` }
                    ] : []),
                    ...(selectedApp.documents && selectedApp.documents.referralLetter ? [
                      { key: 'referralLetter', title: 'Muassasadan yoʻllanma xat', icon: '📑', defaultFile: `yollanma_xat_${selectedApp.id}.pdf` }
                    ] : [])
                  ].map((docDef) => {
                    const docFilename = (selectedApp.documents && selectedApp.documents[docDef.key]) || docDef.defaultFile;
                    const docViewUrl = `${API_BASE}/api/documents/view/${encodeURIComponent(docFilename)}`;
                    const docDownloadUrl = `${API_BASE}/api/documents/download/${encodeURIComponent(docFilename)}`;
                    return (
                      <div className="vd-doc-item-row" key={docDef.key}>
                        <div className="vd-doc-item-left">
                          <span className="vd-doc-item-icon">{docDef.icon}</span>
                          <div className="vd-doc-item-info">
                            <strong className="vd-doc-item-title">{docDef.title}</strong>
                            <span className="vd-doc-item-file">📁 {docFilename}</span>
                          </div>
                        </div>
                        <div className="vd-doc-item-actions">
                          <button
                            type="button"
                            className="btn-doc-act view"
                            onClick={() => setPreviewDoc({
                              title: docDef.title,
                              filename: docFilename,
                              url: docViewUrl,
                              downloadUrl: docDownloadUrl
                            })}
                            title="Hujjatni modalda ochib koʻrish"
                          >
                            👁️ Ochib koʻrish
                          </button>
                          <a
                            href={docViewUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="btn-doc-act view"
                            style={{ textDecoration: 'none', background: '#f1f5f9', color: '#475569' }}
                            title="Alohida yangi oynada ochish"
                          >
                            ↗ Ochish
                          </a>
                          <a
                            href={docDownloadUrl}
                            download={docFilename}
                            className="btn-doc-act download"
                            title="Hujjatni yuklab olish"
                          >
                            📥 Yuklab olish
                          </a>
                        </div>
                      </div>
                    );
                  })}

                  {selectedApp.examResult?.resultFile && (
                    <div className="vd-doc-item-row" style={{ background: '#fef3c7', borderColor: '#fcd34d' }}>
                      <div className="vd-doc-item-left">
                        <span className="vd-doc-item-icon">🏆</span>
                        <div className="vd-doc-item-info">
                          <strong className="vd-doc-item-title" style={{ color: '#b45309' }}>Rasmiy imtihon bayonnomasi (PDF)</strong>
                          <span className="vd-doc-item-file">📁 {selectedApp.examResult.resultFile}</span>
                        </div>
                      </div>
                      <div className="vd-doc-item-actions">
                        <button
                          type="button"
                          className="btn-doc-act view"
                          onClick={() => setPreviewDoc({
                            title: 'Rasmiy imtihon bayonnomasi',
                            filename: selectedApp.examResult.resultFile,
                            url: `${API_BASE}/api/documents/view/${encodeURIComponent(selectedApp.examResult.resultFile)}`,
                            downloadUrl: `${API_BASE}/api/documents/download/${encodeURIComponent(selectedApp.examResult.resultFile)}`
                          })}
                        >
                          👁️ Ochib koʻrish
                        </button>
                        <a
                          href={`${API_BASE}/api/documents/download/${encodeURIComponent(selectedApp.examResult.resultFile)}`}
                          download={selectedApp.examResult.resultFile}
                          className="btn-doc-act download"
                        >
                          📥 Yuklab olish
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="modal-actions" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '14px', paddingTop: '14px', borderTop: '1px solid #e2e8f0', background: '#ffffff', flexShrink: 0 }}>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                {selectedApp.status === 'pending' && (
                  <>
                    <button
                      type="button"
                      className="btn-act approve"
                      style={{ padding: '8px 18px', fontSize: '13.5px', borderRadius: '8px', cursor: 'pointer', fontWeight: '700' }}
                      onClick={() => openModal(selectedApp, 'approve')}
                    >
                      ✓ Qabul qilish va sana belgilash
                    </button>
                    <button
                      type="button"
                      className="btn-act reject"
                      style={{ padding: '8px 18px', fontSize: '13.5px', borderRadius: '8px', cursor: 'pointer', fontWeight: '700' }}
                      onClick={() => openModal(selectedApp, 'reject')}
                    >
                      ✕ Rad etish
                    </button>
                  </>
                )}
                {selectedApp.status === 'approved' && (
                  <button
                    type="button"
                    className="btn-act edit-schedule"
                    style={{ padding: '8px 18px', fontSize: '13.5px', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe' }}
                    onClick={() => openModal(selectedApp, 'approve')}
                  >
                    📅 Imtihon joyi va vaqtini tahrirlash
                  </button>
                )}
                {(selectedApp.status === 'approved' || selectedApp.status === 'passed' || selectedApp.status === 'failed') && (
                  <button
                    type="button"
                    className="btn-act-grade"
                    style={{ padding: '8px 18px', fontSize: '13.5px', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', background: '#f59e0b', color: '#ffffff', border: 'none' }}
                    onClick={() => openModal(selectedApp, 'result')}
                  >
                    🏆 Offline imtihon natijasini kiritish
                  </button>
                )}
                <button
                  type="button"
                  className="btn-act edit-perm"
                  style={{
                    padding: '8px 18px',
                    fontSize: '13.5px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '700',
                    background: selectedApp.allowEdit ? '#fef3c7' : '#f8fafc',
                    color: selectedApp.allowEdit ? '#b45309' : '#334155',
                    border: '1px solid #cbd5e1'
                  }}
                  onClick={() => openModal(selectedApp, 'allow-edit')}
                >
                  ✏️ {selectedApp.allowEdit ? 'Tahrirlash ruxsatini koʻrish' : 'Tahrirlashga ruxsat berish'}
                </button>

                <button
                  type="button"
                  className="btn-act delete-app"
                  style={{
                    padding: '8px 18px',
                    fontSize: '13.5px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '700',
                    background: '#fee2e2',
                    color: '#dc2626',
                    border: '1px solid #fecaca'
                  }}
                  onClick={() => handleDeleteApplicant(selectedApp.id, selectedApp.fullName || selectedApp.full_name)}
                  title="Arizani va serverga yuklangan fayllarni butunlay oʻchirish"
                >
                  🗑️ Arizani butunlay oʻchirish
                </button>
              </div>
              <button className="btn-modal-cancel" onClick={() => setModalType(null)}>Yopish</button>
            </div>
          </div>
        </div>
      )}

      {/* RESULT MODAL */}
      {modalType === 'result' && selectedApp && (
        <div className="admin-modal-overlay" onClick={() => setModalType(null)}>
          <div
            className="admin-modal-box large"
            onClick={(e) => e.stopPropagation()}
            style={{ maxHeight: '92vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
          >
            <div className="modal-top" style={{ flexShrink: 0, marginBottom: '14px', paddingBottom: '12px', borderBottom: '1px solid #e2e8f0' }}>
              <h3 style={{ color: '#0f2b5c', margin: 0, display: 'flex', alignItems: 'center', gap: '8px', fontSize: '17px', fontWeight: '700' }}>
                🏆 Offline imtihon natijasi va komissiya bayonnomasini kiritish
              </h3>
              <button
                type="button"
                className="btn-close-modal"
                onClick={() => setModalType(null)}
                title="Yopish (Esc)"
                aria-label="Yopish"
              >
                ✕
              </button>
            </div>

            <form
              onSubmit={handleResultSubmit}
              className="modal-result-form"
              style={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                minHeight: 0,
                overflow: 'hidden',
                margin: 0
              }}
            >
              <div
                className="admin-modal-scrollable-body"
                style={{
                  overflowY: 'auto',
                  flex: 1,
                  minHeight: 0,
                  paddingRight: '8px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '14px'
                }}
              >
                <div className="modal-app-summary" style={{ margin: 0, flexShrink: 0 }}>
                  <div>
                    <strong>{selectedApp.fullName || selectedApp.full_name}</strong>
                    <span style={{ display: 'block', fontSize: '12.5px', color: '#64748b' }}>
                      Fan: {selectedApp.specialization} • JSHSHIR: {selectedApp.passportNumber || selectedApp.passport_number}
                    </span>
                  </div>
                  <div>
                    <span className="badge-offline-date" style={{ background: '#ecfdf5', color: '#059669', padding: '4px 10px', borderRadius: '6px', fontWeight: '700', fontSize: '12px' }}>
                      📅 Imtihon: {selectedApp.testDate || '2026-10-25'}
                    </span>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '14px' }}>
                  <div className="form-group-modal">
                    <label>Imtihon toʻplagan bali (0 - 100):</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.5"
                      value={examScore}
                      onChange={(e) => {
                        const val = parseFloat(e.target.value) || 0;
                        setExamScore(val);
                        if (val >= 60) {
                          setExamResultStatus('passed');
                        } else {
                          setExamResultStatus('failed');
                        }
                      }}
                      required
                    />
                    <small style={{ color: examScore >= 60 ? '#16a34a' : '#dc2626', fontWeight: '600' }}>
                      {examScore >= 60 ? '✓ Oʻtish bali toʻplandi (Ijobiy, >= 60)' : '⚠️ Oʻtish bali toʻplanmadi (< 60)'}
                    </small>
                  </div>

                  <div className="form-group-modal">
                    <label>Natija holati:</label>
                    <select
                      value={examResultStatus}
                      onChange={(e) => setExamResultStatus(e.target.value)}
                      style={{ padding: '9px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13.5px', fontWeight: '600' }}
                    >
                      <option value="passed">✓ Sinovdan oʻtdi (Muvaffaqiyatli)</option>
                      <option value="failed">✕ Sinovdan oʻta olmadi (Qayta topshirish)</option>
                    </select>
                  </div>
                </div>

                <div className="form-group-modal">
                  <label>Rasmiy bayonnoma / Qayd raqami:</label>
                  <input
                    type="text"
                    placeholder="Masalan: № 2026-041"
                    value={examCertNumber}
                    onChange={(e) => setExamCertNumber(e.target.value)}
                  />
                </div>

                <div className="form-group-modal">
                  <label>Komissiya hayʼati qarori va xulosasi:</label>
                  <textarea
                    rows="3"
                    placeholder="Talabgorning imtihon javoblari yuzasidan komissiya bayonnomasi xulosasi..."
                    value={examResultNotes}
                    onChange={(e) => setExamResultNotes(e.target.value)}
                  ></textarea>
                </div>

                <div className="form-group-modal">
                  <label>Rasmiy imtihon bayonnomasi (PDF hujjat biriktirish - ixtiyoriy):</label>
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={(e) => setResultPdfFile(e.target.files[0] || null)}
                    style={{ padding: '8px', border: '1.5px dashed #cbd5e1', borderRadius: '8px', background: '#f8fafc' }}
                  />
                  <small style={{ color: '#64748b' }}>
                    Komissiya imzolari qoʻyilgan skaner qilingan PDF qaydnomani yuklashingiz mumkin. Bu hujjat talabgor kabinetida ham koʻrinadi.
                  </small>
                </div>
              </div>

              {/* Permanently visible action buttons pinned at the bottom */}
              <div
                className="modal-actions"
                style={{
                  flexShrink: 0,
                  marginTop: '14px',
                  paddingTop: '14px',
                  borderTop: '1px solid #e2e8f0',
                  background: '#ffffff',
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '12px'
                }}
              >
                <button
                  type="button"
                  className="btn-modal-cancel"
                  onClick={() => setModalType(null)}
                >
                  Bekor qilish
                </button>
                <button
                  type="submit"
                  className="btn-modal-save"
                  disabled={actionLoading}
                  style={{
                    background: '#f59e0b',
                    color: '#ffffff',
                    fontWeight: '700',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  {actionLoading ? 'Saqlanmoqda...' : '✓ Natijani saqlash va talabgorga eʼlon qilish'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* DOCUMENT PREVIEW MODAL */}
      {previewDoc && (
        <div className="admin-modal-overlay doc-preview-overlay" onClick={() => setPreviewDoc(null)}>
          <div className="admin-modal-box doc-preview-modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="modal-top">
              <div className="doc-preview-head-info">
                <h3>{previewDoc.title}</h3>
                <span className="doc-file-tag">📁 {previewDoc.filename}</span>
              </div>
              <div className="doc-preview-head-actions">
                <a
                  href={previewDoc.downloadUrl || `/api/documents/download/${encodeURIComponent(previewDoc.filename)}`}
                  download={previewDoc.filename}
                  className="btn-open-external"
                  style={{ background: '#16a34a', textDecoration: 'none' }}
                  title="Faylni kompyuterga yuklab olish"
                >
                  📥 Yuklab olish
                </a>
                <a
                  href={previewDoc.url}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-open-external"
                  title="Alohida yangi oynada ochish"
                >
                  ↗ Yangi oynada ochish
                </a>
                <button
                  type="button"
                  className="btn-close-modal"
                  onClick={() => setPreviewDoc(null)}
                  title="Yopish (Esc)"
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="doc-preview-iframe-wrapper">
              <iframe
                src={previewDoc.url}
                title={previewDoc.title}
                className="doc-preview-iframe"
              />
            </div>

            <div className="modal-actions doc-preview-footer">
              <span className="doc-verified-badge">✓ Turon universiteti rasmiy axborot tizimi orqali himoyalangan</span>
              <button className="btn-modal-cancel" onClick={() => setPreviewDoc(null)}>Yopish</button>
            </div>
          </div>
        </div>
      )}

      {/* REVIEW ADD / EDIT MODAL */}
      {reviewModal && (
        <div className="admin-modal-overlay" onClick={() => setReviewModal(null)}>
          <div className="admin-modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="modal-top">
              <h3>
                {reviewModal === 'add' ? 'Yangi xizmat sharhi qoʻshish' : 'Xizmat sharhini tahrirlash'}
              </h3>
              <button
                type="button"
                className="btn-close-modal"
                onClick={() => setReviewModal(null)}
                title="Yopish (Esc)"
              >
                ✕
              </button>
            </div>

            {reviewSaveMsg && (
              <div className="service-modal-success-box" style={{ background: '#ecfdf5', color: '#065f46', border: '1px solid #a7f3d0', padding: '10px 14px', borderRadius: '8px', marginBottom: '16px', fontWeight: 600 }}>
                ✓ {reviewSaveMsg}
              </div>
            )}

            <form onSubmit={handleSaveReview} className="modal-user-form">
              <div className="form-group-modal">
                <label>Qaysi xizmat uchun:</label>
                <select
                  value={reviewForm.serviceId}
                  onChange={(e) => setReviewForm({ ...reviewForm, serviceId: e.target.value })}
                  disabled={reviewModal === 'edit'}
                >
                  {services.map(s => (
                    <option key={s.id} value={s.id}>
                      {s.title}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group-modal">
                <label>Talabgor / Foydalanuvchi F.I.Sh.:</label>
                <input
                  type="text"
                  placeholder="Masalan: Karimova Shahlo"
                  value={reviewForm.userName}
                  onChange={(e) => setReviewForm({ ...reviewForm, userName: e.target.value })}
                  required
                />
              </div>

              <div className="form-group-modal">
                <label>Qoʻyilgan baho (1 dan 5 gacha yulduz):</label>
                <div className="star-rating-selector">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      type="button"
                      key={star}
                      className={`star-pick-btn ${star <= reviewForm.rating ? 'active' : ''}`}
                      onClick={() => setReviewForm({ ...reviewForm, rating: star })}
                    >
                      ★
                    </button>
                  ))}
                  <span className="star-rating-val-display">{reviewForm.rating} / 5</span>
                </div>
              </div>

              <div className="form-group-modal full-width">
                <label>Sharh va fikr matni:</label>
                <textarea
                  rows="3"
                  placeholder="Xizmat sifati, tezligi va qulayligi haqida fikr..."
                  value={reviewForm.comment}
                  onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
                  required
                ></textarea>
                <span className="input-field-tip">
                  Ushbu sharh saqlanganda xizmatning oʻrtacha bahosi avtomatik qayta hisoblanadi va butun saytda yangilanadi.
                </span>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-modal-cancel"
                  onClick={() => setReviewModal(null)}
                >
                  Bekor qilish
                </button>
                <button
                  type="submit"
                  className="btn-modal-save"
                  disabled={actionLoading}
                >
                  {actionLoading ? 'Saqlanmoqda...' : 'Saqlash va reytingni yangilash'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
